  import { t, getLocale } from '/src/i18n.js';

  class NotificationInbox extends HTMLElement {

    // ── A. Shadow DOM setup ────────────────────────────────────────────────
    constructor() {
      super();
      this._shadow = this.attachShadow({ mode: "open" });
      this._items  = [];   // internal working copy
      this._filter = "all"; // "all" | "unread" | "read"
    }

    // ── B. Lifecycle ──────────────────────────────────────────────────────
    connectedCallback() {
      this._render();
      this._bindEvents();
    }

    // ── C. JS property setter ──────────────────────────────────────────────
    // Accepts the notifications array from your DB.
    set notifications(data) {
      // Deep-clone so we don't mutate caller's array
      this._items = Array.isArray(data)
        ? data.map(n => ({ ...n }))
        : [];
      this._renderList();
      this._renderHeader();
      this._emitUnreadCount();
    }

    // ── D. Attribute → property bridge ────────────────────────────────────
    static get observedAttributes() { return ["notifications"]; }
    attributeChangedCallback(name, _, newVal) {
      if (name === "notifications") {
        try { this.notifications = JSON.parse(newVal); } catch {}
      }
    }

    // ── E. Public API: get unread count ───────────────────────────────────
    get unreadCount() {
      return this._items.filter(n => !n.read).length;
    }

    // ── F. Format timestamp ───────────────────────────────────────────────
    parseLocalDate(ts) {
      const [datePart, timePart] = ts.split("T");
      const [y, m, d] = datePart.split("-").map(Number);
      const [h, min, s] = timePart.split(":").map(Number);

      return new Date(y, m - 1, d, h, min, s);
    }

    _fmtTs(ts) {
      const date = this.parseLocalDate(ts);
      const now  = new Date();
      const diff = (now - date) / 1000; // seconds
      
      if (diff < 60)    return t("inbox-just-now");
      if (diff < 3600)  return t("inbox-m-ago").replace("{m}", Math.floor(diff / 60));
      if (diff < 86400) return t("inbox-h-ago").replace("{h}", Math.floor(diff / 3600));
      const days = Math.floor(diff / 86400);
      if (days === 1)   return t("inbox-yesterday");
      if (days < 7)     return t("inbox-d-ago").replace("{d}", days);
      return date.toLocaleDateString(getLocale(), { day: "numeric", month: "short" });
    }

    // ── G. Category config ────────────────────────────────────────────────
    _catConfig(cat) {
      const map = {
        info:    { color: "#2a6496", bg: "#ddeef9", icon: `<svg width="12" height="12" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1.3"/><path d="M7 6.5V10M7 4.5v.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>` },
        warning: { color: "#92450a", bg: "#fef3c7", icon: `<svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 2L1.5 12h11L7 2z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M7 6v3M7 10.5v.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>` },
        success: { color: "#0F6E56", bg: "#e6f4ea", icon: `<svg width="12" height="12" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1.3"/><path d="M4.5 7l2 2 3-3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>` },
        alert:   { color: "#c0392b", bg: "#fdecea", icon: `<svg width="12" height="12" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1.3"/><path d="M7 4v4M7 9.5v.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>` },
      };
      return map[cat] || map.info;
    }

    // ── H. Filtered + sorted list ─────────────────────────────────────────
    _getFiltered() {
      return this._items
        .filter(n => {
          if (this._filter === "unread") return !n.read;
          if (this._filter === "read")   return  n.read;
          return true;
        })
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }

    // ── I. Build HTML for one notification row ────────────────────────────
    _rowHTML(n) {
      const cat     = this._catConfig(n.category || "info");
      const initials = (n.title.slice(0, 2)).toUpperCase();

      const avatarHTML = n.avatar
        ? `<img class="n-avatar" src="${n.avatar}" alt=""
               onerror="this.style.display='none';this.nextElementSibling.style.display='flex'">
           <div class="n-avatar n-avatar-ph" style="display:none;background:${cat.bg};color:${cat.color}">${initials}</div>`
        : `<div class="n-avatar n-avatar-ph" style="background:${cat.bg};color:${cat.color}">
             <span class="n-cat-icon" style="color:${cat.color}">${cat.icon}</span>
           </div>`;

      return `
        <div class="n-row${n.read ? " is-read" : ""}" data-id="${n.id}" role="listitem">
          <div class="n-avatar-col">
            <!--
            ${avatarHTML}
            -->
            <!--${!n.read ? '<span class="n-dot" aria-label="Unread"></span>' : ''}-->
            <!--
            ${n.read
                ? `<span class="n-chip n-chip-read">Read</span>`
                : `<span class="n-chip n-chip-unread">Unread</span>`
              }
            -->
            ${n.read
                ? `<span class="n-chip n-chip-read">${this._readMessageSVG(25)}</span>`
                : `<span class="n-chip n-chip-unread">${this._messageSVG(25)}</span>`
              }
          </div>
          <div class="n-body">
            <div class="n-top">
              <span class="n-title">${n.title}</span>
              <span class="n-ts" title="${n.timestamp}">${this._fmtTs(n.timestamp)}</span>
            </div>
            <div class="n-message">
              <span class="n-text">${n.body}</span>
            </div>
            <!--
            <div class="n-chips">
              <span class="n-chip" style="background:${cat.bg};color:${cat.color}">
                ${cat.icon} ${n.category || "info"}
              </span>
              
            </div>
            -->
          </div>
          <div class="n-actions">
            <!--
            ${!n.read ? `
            <button class="n-btn n-btn-read" data-id="${n.id}" title="${t("inbox-mark-read")}" aria-label="${t("inbox-mark-read")}">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M2 9l4 4 8-8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>` : ''}
            -->
            <button class="n-btn n-btn-del" data-id="${n.id}" title="${t("inbox-delete")}" aria-label="${t("inbox-delete-notification")}">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M3 4h10M6 4V2h4v2M5 4v9a1 1 0 001 1h4a1 1 0 001-1V4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>`;
    }

    _messageSVG(size = 18) {
      return `<svg fill="#000000" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${size}" height="${size}" viewBox="0 0 100 100" enable-background="new 0 0 100 100" xml:space="preserve">
        <g fill="#ffffff">
          <path d="M13.266,81h73.469C89.087,81,91,78.967,91,76.614V26.165c0-0.009,0.003,0.043,0.003,0.034c0-2.348-1.914-4.199-4.267-4.199
            h-0.002H13.612h-0.347C10.913,22,9,23.795,9,26.147v50.467C9,78.967,10.913,81,13.266,81z M38.525,49.122L13,74.442V29.89
            L38.525,49.122z M87,30.186v42.289L62.914,48.581L87,30.186z M59.723,51.02c0.044,0.053,0.082,0.169,0.132,0.22L85.764,77H16.221
            l25.533-25.387l7.481,5.607c0.356,0.268,0.779,0.388,1.203,0.388c0.428,0,0.855-0.144,1.214-0.418L59.723,51.02z M86.047,26
            L50.431,53.202L14.326,26H86.047z"/>
        </g>
        </svg>`;
    }

    _readMessageSVG(size = 18) {
      return `<svg width="${size}" height="${size}" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" xmlns:xlink="http://www.w3.org/1999/xlink" enable-background="new 0 0 512 512">
        <g>
          <g fill="#000000">
            <path d="m496.4,244.6l-88.1-61.6v-161.6c0-5.8-4.7-10.4-10.4-10.4h-283.8c-5.8,0-10.4,4.7-10.4,10.4v161.6l-88.1,61.6c0,0-4.6,2.9-4.6,8.7v237.3c0,5.8 4.7,10.4 10.4,10.4h469.1c5.8,0 10.4-4.7 10.4-10.4v-237.4c0.1-5.8-4.5-8.6-4.5-8.6zm-24,8.6l-64.1,44.8v-89.6l64.1,44.8zm-347.8-221.3h262.9v280.6l-131.5,90.2-131.4-90.2v-280.6zm-20.9,176.5v89.6l-64.1-44.8 64.1-44.8zm-71.8,271.7v-206.9l218.2,150.7c5.3,3.4 10,1.2 11.8,0l218.2-150.7v206.9h-448.2z"/>
            <path d="m172.9,96.3h166.2c5.8,0 10.4-4.7 10.4-10.4 0-5.8-4.7-10.4-10.4-10.4h-166.2c-5.8,0-10.4,4.7-10.4,10.4 0,5.8 4.6,10.4 10.4,10.4z"/>
            <path d="m172.9,156.6h166.2c5.8,0 10.4-4.7 10.4-10.4 0-5.8-4.7-10.4-10.4-10.4h-166.2c-5.8,0-10.4,4.7-10.4,10.4 0,5.7 4.6,10.4 10.4,10.4z"/>
            <path d="m172.9,216.8h166.2c5.8,0 10.4-4.7 10.4-10.4 0-5.8-4.7-10.4-10.4-10.4h-166.2c-5.8,0-10.4,4.7-10.4,10.4 0,5.7 4.6,10.4 10.4,10.4z"/>
          </g>
        </g>
      </svg>`;
          }

    // ── J. Re-render the list section ─────────────────────────────────────
    _renderList() {
      const listEl = this._shadow.getElementById("n-list");
      if (!listEl) return;
      const filtered = this._getFiltered();

      if (!filtered.length) {
        const emptyMsg = this._filter === "unread"
          ? t("inbox-no-unread")
          : this._filter === "read"
            ? t("inbox-no-read")
            : t("inbox-no-notifications");
        listEl.innerHTML = `
          <div class="n-empty">
            <svg width="36" height="36" viewBox="0 0 40 40" fill="none" opacity="0.25">
              <path d="M20 6a11 11 0 00-11 11v5l-3 4h28l-3-4v-5A11 11 0 0020 6z" stroke="#1a1a1a" stroke-width="1.8" stroke-linejoin="round"/>
              <path d="M17 34a3 3 0 006 0" stroke="#1a1a1a" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
            <p>${emptyMsg}</p>
          </div>`;
        return;
      }

      listEl.innerHTML = filtered.map(n => this._rowHTML(n)).join("");

      // Bind row-level events
      listEl.querySelectorAll(".n-btn-read").forEach(btn => {
        btn.addEventListener("click", e => {
          e.stopPropagation();
          this._toggleRead(Number(btn.dataset.id));
        });
      });
      listEl.querySelectorAll(".n-btn-del").forEach(btn => {
        btn.addEventListener("click", e => {
          e.stopPropagation();
          this._deleteItem(Number(btn.dataset.id));
        });
      });
      // Click anywhere on a row to toggle read/unread
      listEl.querySelectorAll(".n-row").forEach(row => {
        row.addEventListener("click", e => {
          e.stopPropagation();
          console.log("Click event on row %s", row.dataset.id);
          this._toggleRead(Number(row.dataset.id));
        });
      });
    }

    // ── K. Re-render header count + "mark all" visibility ─────────────────
    _renderHeader() {
      const countEl   = this._shadow.getElementById("n-count");
      const markAllEl = this._shadow.getElementById("n-mark-all");
      if (!countEl) return;

      const total   = this._getFiltered().length;
      const unread  = this._items.filter(n => !n.read).length;

      countEl.textContent = total + " " + (total !== 1 ? t("inbox-notification-pl") : t("inbox-notification-sg"));

      if (markAllEl) {
        markAllEl.style.display = unread > 0 ? "inline-flex" : "none";
      }
    }

    // ── L. Toggle read/unread state ──────────────────────────────────────
    _toggleRead(id) {
      const item = this._items.find(n => n.id === id);
      if (!item) return;
      item.read = !item.read;
      this._renderList();
      this._renderHeader();
      this._emitUnreadCount();
      const eventName = item.read ? "notification-read" : "notification-unread";
      this._dispatch(eventName, { ...item });
    }

    // ── M. Mark all unread as read ─────────────────────────────────────────
    _markAllRead() {
      const unreadIds = this._items.filter(n => !n.read).map(n => n.id);
      if (!unreadIds.length) return;
      this._items.forEach(n => { n.read = true; });
      this._renderList();
      this._renderHeader();
      this._emitUnreadCount();
      this._dispatch("notification-read-all", { ids: unreadIds });
    }

    // ── N. Delete a notification ──────────────────────────────────────────
    _deleteItem(id) {
      const idx = this._items.findIndex(n => n.id === id);
      if (idx === -1) return;

      // Animate out
      const row = this._shadow.querySelector(`.n-row[data-id="${id}"]`);
      if (row) {
        row.style.transition = "opacity 0.18s, transform 0.18s";
        row.style.opacity    = "0";
        row.style.transform  = "translateX(12px)";
        setTimeout(() => {
          this._items.splice(idx, 1);
          this._renderList();
          this._renderHeader();
          this._emitUnreadCount();
        }, 180);
      } else {
        this._items.splice(idx, 1);
        this._renderList();
        this._renderHeader();
        this._emitUnreadCount();
      }

      this._dispatch("notification-delete", { id });
    }

    // ── O. Fire unread-count-change event ─────────────────────────────────
    _emitUnreadCount() {
      this._dispatch("unread-count-change", { unreadCount: this.unreadCount });
    }

    // ── P. Fire a custom event ────────────────────────────────────────────
    _dispatch(name, detail) {
      this.dispatchEvent(new CustomEvent(name, {
        detail, bubbles: true, composed: true
      }));
    }

    // ── Q. Switch filter tab ──────────────────────────────────────────────
    _switchFilter(f) {
      this._filter = f;
      this._shadow.querySelectorAll(".n-tab").forEach(t =>
        t.classList.toggle("active", t.dataset.filter === f)
      );
      this._renderList();
      this._renderHeader();
    }

    // ── R. Bind toolbar events ────────────────────────────────────────────
    _bindEvents() {
      this._shadow.querySelectorAll(".n-tab").forEach(t =>
        t.addEventListener("click", () => this._switchFilter(t.dataset.filter))
      );
      this._shadow.getElementById("n-mark-all")
        ?.addEventListener("click", () => this._markAllRead());
    }

    // ── S. Full initial render ────────────────────────────────────────────
    _render() {
      this._shadow.innerHTML = `
        <style>
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

          :host {
            display: block;
            color: #1a1a1a;
          }

          .card {
            background: #fff;
            border: 0.5px solid #e0dfd7;
            border-radius: 12px;
            min-height: calc(100vh - 190px);
            display: flex;
            flex-direction: column;
          }
 
          .header {
            padding: 14px 16px 12px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            /*border-bottom: 0.5px solid #e0dfd7;*/
            flex-wrap: wrap;
            gap: 6px;
          }
 
          .title {
            font-size: 15px;
            font-weight: 500;
            color: #1a1a1a;
            display: flex;
            align-items: center;
            gap: 8px;
          }
 
          .title-icon {
            width: 28px; height: 28px;
            background: #5D4037;
            border-radius: 8px;
            display: flex; align-items: center; justify-content: center;
            flex-shrink: 0;
          }

          .card-body {
            padding: 2px 16px 16px 16px;
          }

          /* ── Toolbar ── */
          .n-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            margin-bottom: 10px;
            flex-wrap: wrap;
          }

          .n-tabs {
            display: flex;
            gap: 2px;
            background: #ededea;
            border-radius: 8px;
            padding: 3px;
          }

          .n-tab {
            font: inherit;
            font-size: 12px;
            font-weight: 500;
            padding: 5px 12px;
            border-radius: 6px;
            border: none;
            background: transparent;
            color: #888;
            cursor: pointer;
            transition: background 0.12s, color 0.12s;
            white-space: nowrap;
          }
          .n-tab.active {
            background: #fff;
            color: #1a1a1a;
            border: 0.5px solid #e0dfd7;
          }

          /* ── Sub-header ── */
          .n-subheader {
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 12px;
            color: #888;
            margin-bottom: 8px;
            min-height: 26px;
          }

          .n-mark-all {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            font: inherit;
            font-size: 12px;
            font-weight: 500;
            color: #0F6E56;
            background: none;
            border: none;
            cursor: pointer;
            padding: 3px 0;
            transition: opacity 0.15s;
          }
          .n-mark-all:hover { opacity: 0.7; }

          /* ── List ── */
          .n-list {
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          /* ── Row ── */
          .n-row {
            background: #fff;
            border: 0.5px solid #e0dfd7;
            border-radius: 12px;
            padding: 13px 12px 13px 14px;
            display: grid;
            grid-template-columns: 48px 1fr auto;
            gap: 0 12px;
            align-items: start;
            cursor: pointer;
            transition: border-color 0.15s, box-shadow 0.15s;
            position: relative;
          }
          .n-row:hover { border-color: #bbb; }
          .n-row:focus-visible { outline: 2px solid #4a90d9; outline-offset: 2px; }

          /* Unread rows have a subtle left accent */
          .n-row:not(.is-read) {
            border: 1.5px solid #1a1a1a;
          }

          .n-row.is-read {
            background: #fafaf8;
            cursor: pointer;
          }
          .n-row.is-read:hover { border-color: #1a1a1a; }
          .n-row.is-read .n-title { font-weight: 400; color: #555; }

          /* ── Avatar col ── */
          .n-avatar-col {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding-top: 2px;
          }

          .n-avatar {
            width: 40px; height: 40px;
            border-radius: 50%;
            object-fit: cover;
            border: 0.5px solid #e0dfd7;
            flex-shrink: 0;
          }

          .n-avatar-ph {
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 500;
          }

          .n-cat-icon {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .n-dot {
            width: 7px; height: 7px;
            border-radius: 50%;
            background: #1a1a1a;
            flex-shrink: 0;
          }

          /* ── Body ── */
          .n-body { 
            min-width: 0; 
            display: flex;
            flex-direction: column;
          }

          .n-top {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 8px;
            margin-bottom: 4px;
          }

          .n-title {
            font-size: 13px;
            font-weight: 500;
            color: #1a1a1a;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .n-ts {
            font-size: 11px;
            color: #aaa;
            flex-shrink: 0;
            white-space: nowrap;
          }
          
          .n-message {
            align-items: baseline;
            text-align: left;
          }

          .n-text {
            font-size: 12px;
            color: #666;
            line-height: 1.5;
            margin-bottom: 8px;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-align: left;
          }

          .n-chips {
            display: flex;
            flex-wrap: wrap;
            gap: 5px;
          }

          .n-chip {
            display: inline-flex;
            align-items: center;
            gap: 3px;
            font-size: 10px;
            font-weight: 500;
            padding: 2px 7px;
            border-radius: 99px;
            min-width: 40px;
            min-height: 40px;
          }

          .n-chip-unread {
            background: #5D4037;
            color: #fff;
          }

          .n-chip-read {
            background: #f0ede8;
            color: #aaa;
            border: 0.5px solid #e0dfd7;
          }

          /* ── Actions ── */
          .n-actions {
            display: flex;
            flex-direction: column;
            gap: 6px;
            padding-top: 2px;
          }

          .n-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px; height: 28px;
            border-radius: 7px;
            border: 0.5px solid #e0dfd7;
            background: #fff;
            color: #888;
            cursor: pointer;
            transition: background 0.12s, color 0.12s, border-color 0.12s;
          }

          .n-btn-read:hover { background: #e6f4ea; color: #0F6E56; border-color: #b7dfbf; }
          .n-btn-del:hover  { background: #fdecea; color: #c0392b; border-color: #f1c0bb; }

          /* ── Empty state ── */
          .n-empty {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
            padding: 3rem 1rem;
            color: #bbb;
            font-size: 13px;
            text-align: center;
          }

          /* ── Dark mode ── */
          /*
          @media (prefers-color-scheme: dark) {
            .n-tabs     { background: #2a2a28; }
            .n-tab.active { background: #1e1e1c; color: #f0ede8; border-color: #333330; }
            .n-tab      { color: #666; }
            .n-row      { background: #1e1e1c; border-color: #333330; }
            .n-row:not(.is-read) { border-left-color: #f0ede8; }
            .n-row.is-read { background: #1a1a18; }
            .n-row.is-read .n-title { color: #666; }
            .n-title    { color: #f0ede8; }
            .n-text     { color: #888; }
            .n-dot      { background: #f0ede8; }
            .n-btn      { background: #1e1e1c; border-color: #333330; color: #666; }
            .n-chip-read { background: #2a2a28; color: #555; border-color: #333; }
            .n-chip-unread { background: #f0ede8; color: #1a1a1a; }
            .n-avatar   { border-color: #333330; }
          }
          */

          /* ── Responsive ── */
          @media (max-width: 400px) {
            .n-row { padding: 5px; gap: 0 8px; grid-template-columns: 40px 1fr auto; }
            .n-title { font-size: 12px; }
            .n-text  { font-size: 11px; }
          }
        </style>

        <div class="card">
          <div class="header">
            <div class="title">
              <div class="title-icon">${this._messageSVG(16)}</div>
              ${t("inbox")}
            </div>
          </div>

          <div class="card-body">
            <div class="n-toolbar">
              <div class="n-tabs" role="tablist">
                <button class="n-tab active" data-filter="all"    role="tab">${t("inbox-all")}</button>
                <button class="n-tab"        data-filter="unread" role="tab">${t("inbox-unread")}</button>
                <button class="n-tab"        data-filter="read"   role="tab">${t("inbox-read")}</button>
              </div>
            </div>

            <div class="n-subheader">
              <span id="n-count">0 ${t("inbox-notification-pl")}</span>
              <button class="n-mark-all" id="n-mark-all" style="display:none">
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                  <path d="M1.5 8l3.5 3.5 7.5-7" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                ${t("inbox-mark-all-read")}
              </button>
            </div>

            <div class="n-list" id="n-list" role="list"></div>
          </div>  
        </div>
      `;
    }
  }

export { NotificationInbox };